import createDebug from 'debug';
import eventemitter3 from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import EnvironmentHelper from '../EnvironmentHelper/EnvironmentHelper';
import UserHelper from '../UserHelper';
import { BombBombApiHelper } from '../ApiHelper/BombBombApiHelper';
import { DurableUploadStrategy } from '../DurableVideo/DurableUploadStrategy';
import { AppConfig } from '../AppConfig';
import { VesData } from '../VesData';
import { getUploadOptimizedVideo } from './MdatMover';
import {
  AudioCodecs,
  DurableRecording,
  EventNames,
  Size,
  VideoCodecs,
  validateVideoInput,
  VideoStatus,
} from '@/constants';
import { formatDate, getVideoDetails, logErrorExternally } from '@/functions';

const { CONNECTED, ERROR, PROGRESS_UPDATE, UPLOAD_SUCCESS } = EventNames.RecordingStrategy;

const debug = createDebug('durable:VideoUploader');

const getFileExtension = (file) => {
  const parts = file?.name?.split('.') || [];
  const extension = parts[parts.length - 1] || '';
  return extension.toLowerCase();
};

/**
 * @name VideoUploader
 */
export class VideoUploader extends eventemitter3 {
  /**
   * @param {string} authToken
   * @param {string|null} environment like 'local', 'dev', 'qa', 'prod'
   * @param {object} options
   * @param {boolean} options.forceBleedingEdgeVES
   * whether or not to use Bleeding Edge VES configuration, only valid when environment is local
   * @param {boolean} options.forceLocalhostVES
   * whether or not to use Localhost VES configuration, only valid when environment is local
   * @param {boolean} options.xsrc
   * xsrc or app name to be used as the video_input_source for uploaded videos
   * @param {string|null} options.videoId
   * videoId to be used as the video_id for uploaded videos, auto-generated if not provided
   * @param {boolean} options.showReviewStatus
   * if showReviewStatus is true, the video will be marked as pending upon upload
   */
  constructor(
    authToken,
    environment = null,
    options = {
      forceBleedingEdgeVES: false,
      forceLocalhostVES: false,
      xsrc: null,
      videoId: null,
      showReviewStatus: false,
    },
  ) {
    super();
    this.authToken = authToken;

    if (environment) {
      EnvironmentHelper.setEnvironment(environment);
    }

    // environment will be PROD, QA, DEV, or LOCAL, based on environment constructor arg
    this.environment = EnvironmentHelper.getEnvironment().toUpperCase();
    this.vesData = new VesData(
      this.environment,
      options.forceBleedingEdgeVES,
      options.forceLocalhostVES,
    );

    this.xsrc = options.xsrc || 'module-library';

    AppConfig.xsrc = this.xsrc;

    if (options.videoId) {
      this.videoId = options.videoId;
    }
    this.showReviewStatus = options.showReviewStatus || false;

    this.uploadStrategy = null;
  }

  queue = [];

  getVideoId = () => {
    if (!this.videoId) {
      this.videoId = uuidv4();
    }
    return this.videoId;
  };

  clearVideoId = () => {
    this.videoId = null;
  };

  /**
   * @param {import('./VideoUploader').UploadFile} file
   */
  addFile = async (file) => {
    validateVideoInput(file);
    this.queue.push(file);
    await this.startUpload();
  };

  startUpload = async () => {
    const promises = [];
    this.queue.forEach(async (file) => {
      promises.push(
        this.handleUploadV2(file).catch((error) => {
          console.warn('V2 file upload failed, falling back to V1.', error);
          this.handleUploadV1(file).catch((err) => {
            this.emit('error', this.getVideoId());
            this.clearVideoId();
          });
        }),
      );
    });
    this.queue = [];
    await Promise.all(promises);
  };

  saveRecordingInformation = async (videoId) => {
    const bbApiHelper = new BombBombApiHelper(this.authToken);
    const recordingInformationPath = `videos/${videoId}/recording_information`;
    const methodDebug = debug.extend('saveRecordingInformation');

    const recordingInformation = {
      videoId,
      recordingType: 'uploaded',
      virtualBackgroundType: 'not_applicable',
    };

    methodDebug('Starting...');
    await bbApiHelper.post(recordingInformationPath, {
      body: JSON.stringify(recordingInformation),
    });
    methodDebug('Finished');
  };

  // eslint-disable-next-line class-methods-use-this
  doRequiredClientTransformations = async (file) => {
    // Does this promise just check that the file can be read? It doesn't seem like it's
    // doing anything with the contents.
    await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => {
        reject(new Error('Failed to open file for processing.'));
      };
      reader.onload = () => {
        resolve(new Uint8Array(reader.result));
      };
      try {
        reader.readAsArrayBuffer(file);
      } catch (error) {
        reject(error);
      }
    });

    return getUploadOptimizedVideo(file);
  };

  handleUploadV2 = async (file) => {
    const methodDebug = debug.extend('handleUploadV2');

    methodDebug('Starting...');

    /**
     * We're leaving this here since it takes the browser timing out to fail.
     * This way, we fail early since we know we don't yet support anything else.
     */
    const extension = getFileExtension(file);
    const videoId = this.getVideoId();
    /**
     * We check if the container format is supported for durable upload to VES. If not, we should
     * immediately fall back to the V1 upload. The container formats for upload include ones like
     * MOV that we can't currently process in VES's saveFromUrl, but the handy thing is that the web
     * app will still be able to process them in the markComplete step because it will fall back to
     * Zencoder there, using the durable file URL from this upload. This allows us to avoid a
     * duplicate file upload to the V1 route in some cases. If there is a failure in the
     * markComplete step, we'll still fall back to the V1 upload.
     */
    const canDoDurableUpload =
      DurableRecording.CONTAINER_FORMATS_SUPPORTED_FOR_UPLOAD.includes(extension);
    if (!canDoDurableUpload) {
      const fileTypeNotSupportedMessage = `file type not yet supported in VES: ${extension}`;
      const fallingBackMessage = `${fileTypeNotSupportedMessage}, falling back to V1`;
      const fallingBackError = new Error(fallingBackMessage);
      logErrorExternally(fileTypeNotSupportedMessage, fallingBackError, 'VideoUploader', {
        videoId,
        fileName: file.name,
        extension,
      });
      throw fallingBackError;
    }

    let fileToUse = file;
    const transformDebug = methodDebug.extend('transformFile');
    try {
      /**
       * We try to transform the file to a format that allows us to take advantage of the eager
       * video completion when the file is the right format for that.
       */
      transformDebug('Starting...');
      fileToUse = await this.doRequiredClientTransformations(file);
      transformDebug('Finished');
    } catch (error) {
      logErrorExternally('Failed to transform file', error, 'VideoUploader', {
        fileName: file.name,
        extension,
      });
      /**
       * But if we can't transform the file for some reason, let's just use the original file for
       * the durable upload. Eager video completion most likely work in this scenario, which is
       * okay.
       */
      transformDebug('Failed; using original file', error);
    }

    const newVideo = {
      uploadId: videoId,
      title: file.name,
      subtitle: `Created: ${formatDate(new Date())}`,
      team: '',
      video: {
        id: videoId,
        status: VideoStatus.UPLOADING,
        progress: 0,
        height: null,
        width: null,
        reviewStatus: this.showReviewStatus ? 'PENDING' : null,
      },
      file: fileToUse,
    };
    this.emit('upload', newVideo);

    const uploadDebug = methodDebug.extend('file upload');
    uploadDebug('Starting...');
    const result = await this.uploadFileV2(newVideo);
    uploadDebug('Finished');

    await this.saveVideoToDBV2(result);

    await this.saveRecordingInformation(result.id);

    const video = await getVideoDetails(this.authToken, this.environment, result.id);
    if (video) {
      newVideo.video.shortUrl = video.short_url;
      newVideo.video.fullUrl = video.full_url;
      newVideo.video.height = video.height;
      newVideo.video.width = video.width;
      newVideo.video.animated = video.animated;
      newVideo.video.animatedThumbUrl = video.animated_thumb_url;
      newVideo.video.trueThumbnailUrl = video.true_thumbnail_url;
      newVideo.video.thumbnail = video.available_thumbnail_urls.still;
      newVideo.video.reviewStatus = this.showReviewStatus ? 'PENDING' : null;
    }

    const savedVideoId = result.id;
    newVideo.video.id = savedVideoId;
    newVideo.video.status = VideoStatus.READY;

    methodDebug('Finished', newVideo);

    this.emit('progress', newVideo);
    this.emit('complete', newVideo);

    this.clearVideoId();
  };

  /**
   * @param {File} file
   */
  // eslint-disable-next-line class-methods-use-this
  getFormatV2 = (file) => {
    // TODO: Make more understandable
    const fr = new FileReader();
    return new Promise((resolve, reject) => {
      fr.onload = () => {
        const view = new DataView(fr.result);
        const byteMarkerA = view.getUint32(0);
        const byteMarkerB = view.getUint32(4);
        const byteMarkerC = view.getUint32(8);

        if (byteMarkerA === 0x52494646 && byteMarkerC === 0x41564920) {
          resolve('avi');
        } else if (byteMarkerA === 0x3026b275 && byteMarkerB === 0x8e66cf11) {
          resolve('wmv');
        } else if (byteMarkerA === 0x1a45dfa3) {
          resolve('mkv');
          // eslint-disable-next-line brace-style
        }
        // Also supports WebM
        else if (byteMarkerA === 0x464c5601) {
          resolve('flv');
        } else if (byteMarkerA === 0x000001b3) {
          resolve('mpeg');
        } else if (byteMarkerB === 0x66747970) {
          if (byteMarkerC === 0x4d345620) {
            resolve('m4v');
          } else if (byteMarkerC === 0x71742020) {
            resolve('mov');
          } else {
            resolve('mp4');
          }
        } else {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject();
        }
      };
      if (file.size > 16) {
        fr.readAsArrayBuffer(file.slice(0, 16));
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject();
      }
    });
  };

  uploadFileV2 = async (video) => {
    // We cannot currently check the progress of a file upload with the Fetch
    // API, which is why we're using the XHR approach
    const videoFormat = await this.getFormatV2(video.file);

    const beginConnectingTime = Date.now();

    this.uploadStrategy = new DurableUploadStrategy(
      this.getVideoId(),
      videoFormat,
      beginConnectingTime,
      this.vesData,
      this.authToken,
    );

    const methodDebug = debug.extend('uploadFileV2');

    return new Promise((resolve, reject) => {
      this.uploadStrategy.on(CONNECTED, () => {
        const eventDebug = methodDebug.extend(CONNECTED);
        eventDebug('Connected to durable upload');
        /**
         * We currently hard code the codecs to H264 and AAC. This is because we don't have a way
         * to detect the codecs of the file being uploaded. If the file is not H264 and AAC, the
         * eager completion will fail, but we should still be able to upload and save the file
         * completeVideoRecording and save the file normally.
         *
         * We would need to improve VES to be able to detect the codecs of a file being uploaded
         * when the client doesn't know them. Or we would need to improve the client to be able to
         * detect the codecs of a file being uploaded, maybe using FFmpeg in the browser.
         */
        this.uploadStrategy.setMediaCodecsRecorded(AudioCodecs.AAC, VideoCodecs.H264);

        let offset = 0;
        const chunkSize = Size.ONE_MB;

        while (offset < video.file.size) {
          const chunk = video.file.slice(offset, offset + chunkSize);
          this.uploadStrategy.handleNewDataEvent({ data: chunk });
          offset += chunkSize;
        }

        /**
         * Estimated duration is used for calculating stopping time report but we don't need it for
         * these file uploads
         */
        const noEstimatedDuration = 0;
        this.uploadStrategy.done(noEstimatedDuration);
      });

      this.uploadStrategy.on(PROGRESS_UPDATE, (progress) => {
        const eventDebug = methodDebug.extend(PROGRESS_UPDATE);
        eventDebug('new progress', progress);
        const updatedVideo = { ...video };
        if (progress >= 100) {
          updatedVideo.video.progress = 99;
        } else {
          updatedVideo.video.progress = progress;
        }
        updatedVideo.video.status = VideoStatus.UPLOADING;
        this.emit('progress', updatedVideo);
        if (progress === 100) {
          performance.mark('videoCompletelyUploaded');
        }
      });

      this.uploadStrategy.on(UPLOAD_SUCCESS, (url) => {
        const eventDebug = methodDebug.extend(UPLOAD_SUCCESS);
        eventDebug('Video url', url);
        performance.mark('videoProcessingComplete');
        performance.measure(
          'videoProcessingTime',
          'videoCompletelyUploaded',
          'videoProcessingComplete',
        );
        const [processingMeasure] = performance.getEntriesByName('videoProcessingTime');
        eventDebug('Video processing time in ms', processingMeasure.duration);
        const updatedVideo = { ...video };
        updatedVideo.video.progress = 100;
        updatedVideo.video.status = VideoStatus.PROCESSING;
        resolve({
          id: video.video.id,
          videoName: video.title,
          url,
        });
      });

      this.uploadStrategy.on(ERROR, (recordingError) => {
        console.error('Recording Strategy error', recordingError);
        reject(recordingError);
      });

      this.uploadStrategy.prepareForRecording();
    });
  };

  saveVideoToDBV2 = async (videoSaveResult) => {
    const bbApiHelper = new BombBombApiHelper(this.authToken);
    const markCompletePath = 'videos/live/markComplete';
    const { id, videoName: name, url } = videoSaveResult;
    const savePayload = {
      videoId: id,
      title: name,
      filename: JSON.stringify({ url }),
      deleteOnError: true,
    };

    const methodDebug = debug.extend('saveVideoToDBV2');
    methodDebug('Marking Video Complete...');
    const markCompleteResponse = await bbApiHelper.post(markCompletePath, {
      body: JSON.stringify(savePayload),
    });
    methodDebug('Finished');

    const videoStatus = markCompleteResponse.status.toUpperCase();
    switch (videoStatus) {
      /**
       * If the video is still encoding, we need to poll for the video status until it's ready. This
       * can happen when VES cannot process that particular video type so the video falls back to
       * Zencoder for processing.
       */
      case VideoStatus.ENCODING:
        const videoContainer = {
          uploadId: id,
          title: name,
          subtitle: `Created: ${formatDate(new Date())}`,
          team: '',
          video: {
            id,
            status: VideoStatus.PROCESSING,
            progress: 0,
          },
        };
        await this.pollVideoEncodingStatusV1(videoContainer);
        break;

      case VideoStatus.FAILED:
        // If the video failed to process, throw an error so we can fall back to V1
        throw new Error('Video failed to process');
      default:
        // Otherwise, we assume the video is ready
        break;
    }
  };

  handleUploadV1 = async (file) => {
    const extension = getFileExtension(file);
    const videoId = this.getVideoId();
    // Log an error here so we can track how often we're falling back to V1
    logErrorExternally('Falling back to V1', null, 'VideoUploader', {
      videoId,
      extension,
    });

    const newVideoContainer = {
      uploadId: videoId,
      title: file.name,
      subtitle: `Created: ${formatDate(new Date())}`,
      team: '',
      video: {
        id: videoId,
        status: VideoStatus.UPLOADING,
        progress: 0,
      },
      file,
    };

    this.emit('upload', newVideoContainer);

    const details = await this.uploadV1(newVideoContainer);
    newVideoContainer.video.id = details.info.video_id;
    newVideoContainer.video.status = VideoStatus.PROCESSING;
    newVideoContainer.video.reviewStatus = this.showReviewStatus ? 'PENDING' : null;
    this.emit('progress', newVideoContainer);

    newVideoContainer.video.progress = 0;
    await this.pollVideoEncodingStatusV1(newVideoContainer);
    this.emit('progress', newVideoContainer);

    const finalizedVideoContainer = await this.finalizeV1(newVideoContainer);
    this.emit('progress', finalizedVideoContainer);
    this.emit('complete', newVideoContainer);

    this.clearVideoId();
  };

  finalizeV1 = async (videoContainer) => {
    await this.saveRecordingInformation(videoContainer.video.id);

    const v1GetVideoMetaDataUrl = new URL(EnvironmentHelper.getLegacyApiUrl());
    v1GetVideoMetaDataUrl.searchParams.set('method', 'GetVideoMetaData');
    v1GetVideoMetaDataUrl.searchParams.set('videoId', videoContainer.video.id);
    const details = await (await fetch(v1GetVideoMetaDataUrl.toString())).json();

    const { info } = details;

    const finalizedVideoContainer = {
      ...videoContainer,
      thumbUrl: info.thumb_url,
      height: info.height,
      width: info.width,
      createdFormatted: 'Today',
      animated: info.animated === 1 && !!info.animated_thumb_url,
      animatedThumbUrl: info.animated_thumb_url,
      fullUrl: info.full_url,
      video: {
        ...videoContainer.video,
        status: VideoStatus.READY,
        thumbnail: info.thumb_url,
        shortUrl: info.short_url,
        fullUrl: info.full_url,
        reviewStatus: this.showReviewStatus ? 'PENDING' : null,
      },
    };

    return finalizedVideoContainer;
  };

  pollVideoEncodingStatusV1 = async (videoContainer) => {
    const v1EncodingReportUrl = new URL(EnvironmentHelper.getLegacyApiUrl());
    v1EncodingReportUrl.searchParams.set('method', 'getEncodingReport');
    v1EncodingReportUrl.searchParams.set('id', videoContainer.video.id);
    const response = await (await fetch(v1EncodingReportUrl.toString())).json();

    const report = JSON.parse(response.info);
    const updatedVideoContainer = {
      ...videoContainer,
      video: {
        ...videoContainer.video,
        // There's an issue with the encoding service where it will
        // sometimes report false progress. This way at least, the user
        // will see only increments to encoding.
        progress: Math.max(report.encoding.progress, videoContainer.video.progress),
      },
    };
    this.emit('progress', updatedVideoContainer);
    if (report.video === 2) {
      throw new Error('Encoding service reported a failure');
    } else if (report.video === 1) {
      return report;
    } else {
      return this.pollVideoEncodingStatusV1(updatedVideoContainer);
    }
  };

  uploadV1 = async (videoContainer) => {
    UserHelper.setToken(this.authToken);
    const { userId } = await UserHelper.getUserProfile();

    return new Promise((res, rej) => {
      const apiUrl = EnvironmentHelper.getLegacyApiUrl();

      const data = {
        // eslint-disable-next-line camelcase
        user_id: userId,
        method: 'AsyncVideoUpload',
      };
      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.authToken}`);
      const form = new FormData();
      Object.keys(data).forEach((key) => {
        form.append(key, data[key]);
      });
      const videoId = this.getVideoId();
      if (videoId) {
        form.append('video_id', videoId);
      }

      const name = videoContainer.updatedName || videoContainer.name;
      form.append('import', videoContainer.file, name);
      xhr.upload.onprogress = (event) => {
        const progress = parseInt((event.loaded / event.total) * 100, 10);
        const updatedVideoContainer = { ...videoContainer, video: { ...videoContainer.video } };
        updatedVideoContainer.video.progress = progress;
        updatedVideoContainer.video.status = 'UPLOADING';
        updatedVideoContainer.video.reviewStatus = this.showReviewStatus ? 'PENDING' : null;
        this.emit('progress', updatedVideoContainer);
      };

      xhr.addEventListener('load', (result) => {
        res(JSON.parse(result.target.response));
      });

      xhr.send(form);
    });
  };
}
